<script setup>
/**
 * Multiselect — UI proxy for vue-multiselect.
 *
 * Forwards v-model and all props/attrs to VueMultiselect with sensible defaults.
 * Add more explicit props here as needed; everything else passes through via $attrs.
 *
 * Usage:
 *   <Multiselect v-model="value" :options="list" label="name" track-by="name" />
 */
import VueMultiselect from 'vue-multiselect';
import { useSlots, getCurrentInstance, ref } from 'vue';

defineProps({
    value:   { default: null },
    options: { type: Array, default: () => [] },
});

const slots = useSlots();
const instance = getCurrentInstance();

const vnode = instance.proxy.$vnode;
const hasExternalClass = !!(vnode?.data?.staticClass || vnode?.data?.class);
const defaultClass = hasExternalClass ? null : 'multiselect-mid pl-2';

const inner = ref(null);

defineExpose({
    activate:   () => inner.value?.activate(),
    deactivate: () => inner.value?.deactivate(),
});
</script>

<template>
    <VueMultiselect
        ref="inner"
        v-bind="{
            'select-label': '',
            'deselect-label': '',
            'label': 'name',
            'track-by': 'name',
            ...$attrs,
            value,
            options,
        }"
        :class="defaultClass"
        v-on="instance.proxy.$listeners"
    >
        <template v-if="slots.option" #option="slotProps">
            <slot name="option" v-bind="slotProps" />
        </template>
        <template v-if="slots.singleLabel" #singleLabel="slotProps">
            <slot name="singleLabel" v-bind="slotProps" />
        </template>
        <template v-if="slots.tag" #tag="slotProps">
            <slot name="tag" v-bind="slotProps" />
        </template>
        <template v-if="slots.noResult" #noResult>
            <slot name="noResult" />
        </template>
        <template v-if="slots.noOptions" #noOptions>
            <slot name="noOptions" />
        </template>
        <template v-if="slots.selection" #selection="slotProps">
            <slot name="selection" v-bind="slotProps" />
        </template>
        <template v-if="slots.placeholder" #placeholder>
            <slot name="placeholder" />
        </template>
    </VueMultiselect>
</template>
